import { createEntityAdapter, createSelector, createSlice, Draft, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { PendingUploadAction, RejectedUploadAction } from '@ff-os/shared-utils/types/private-assets/uploadAction';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { PhotoPrintsUploadContext } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsUploadContext';
import { PhotoPrintsPdpState } from '../store';

const adapter = createEntityAdapter<PhotoPrintsJson.Group>();
const adapterSelectors = adapter.getSelectors<PhotoPrintsPdpState>(s => s.project.groups);

export const { actions, reducer } = createSlice({
    name: 'groups',
    initialState: adapter.getInitialState(),
    reducers: {
        setAll: adapter.setAll,
        add: adapter.addOne,
        addMany: adapter.addMany,
        remove: adapter.removeOne,

        updateVariant: (state, action: PayloadAction<{ groupId: string; variantId: string }>) => {
            const { groupId, variantId } = action.payload;
            state.entities[groupId]!.variantId = variantId;
        },

        queuePhotos: (state, action: PayloadAction<{ groupId: string; amount: number }>) => {
            const { groupId, amount } = action.payload;
            state.entities[groupId]!.amountInQueue += amount;
        },
        dequeuePhotos: (state, action: PayloadAction<{ groupId: string; amount: number }>) => {
            const { groupId, amount } = action.payload;
            dequeuePhotos(state, groupId, amount);
        }
    },

    extraReducers: builder => {
        builder.addCase(
            uploadPrivateAsset.pending,
            (state, action: PendingUploadAction<PhotoPrintsUploadContext>) => {
                const { groupId } = action.meta.arg.context;
                dequeuePhotos(state, groupId, 1);
            });
        builder.addCase(
            uploadPrivateAsset.rejected,
            (state, action: RejectedUploadAction<PhotoPrintsUploadContext>) => {
                const { groupId } = action.meta.arg.context;
                dequeuePhotos(state, groupId, 1);
            });
    }
});

const dequeuePhotos = (
    state: Draft<EntityState<PhotoPrintsJson.Group>>,
    groupId: string,
    amount: number
) => {
    const group = state.entities[groupId]!;
    group.amountInQueue = Math.max(group.amountInQueue - amount, 0);
};

export const selectAllGroups = adapterSelectors.selectAll;
export const selectGroupById = adapterSelectors.selectById;

export const selectAmountOfGroups = createSelector([
    selectAllGroups
], groups => groups.length);